import { useIsClient } from "@uidotdev/usehooks";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCompanyLogoImage } from "../../../../helpers/data_management";
import {
  bookmarkJobCompany,
  unBookmarkJobCompany,
} from "../../../../redux/actions/company_action";
import { updateSelectedJob } from "../../../../redux/actions/job_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../../redux/actions/user_action";
import * as types from "../../../../redux/types/company_type";
import { BoxContainer, ButtonStyled, LinkWrapper } from "../styles";
import {
  BookmarkButton,
  ChipContainer,
  CompanyDetailContainer,
  CompanyLogo,
  CompanyName,
  HeaderText,
  JobBoostChip,
  JobCard,
  JobDetailChip,
  JobListContainer,
  JobTitle,
  SaveIcon,
  SparklesIconStyled,
  UnsaveIcon,
  Wrapper,
} from "./styles";

export default function ExpiredSimilarJobs(props) {
  const { similarJobs } = props;

  const isClient = useIsClient();

  // Get recommended jobs from Redux store
  const recommendedJobs = useSelector((state) => state?.jobs?.recommendedJobs);

  return (
    <Wrapper>
      <HeaderText>Similar Jobs You Might Like</HeaderText>
      <JobListContainer>
        {similarJobs?.map((job, index) => (
          <JobCardComponent
            key={job?.id}
            job={job}
            clientSideJob={recommendedJobs}
            bookmarkState={recommendedJobs[index]?.bookmark}
          />
        ))}
      </JobListContainer>
      {isClient && (
        <BoxContainer justifyContent="center">
          <LinkWrapper href={`/jobs`}>
            <ButtonStyled>See More Jobs</ButtonStyled>
          </LinkWrapper>
        </BoxContainer>
      )}
    </Wrapper>
  );
}

function JobCardComponent(props) {
  const { job, bookmarkState } = props;

  const isClient = useIsClient();
  const dispatch = useDispatch();
  const loggedIn = checkAuthentication();

  // Local state for bookmark status and loading state
  const [bookmark, setBookmark] = useState(job?.bookmark);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  // Memoized function to check if work experience should be displayed
  const showWorkExp = useMemo(() => {
    return (currentJob) => {
      return (
        (currentJob.minYearsExperience !== null &&
          currentJob.minYearsExperience > 0) ||
        (currentJob.maxYearsExperience !== null &&
          currentJob.maxYearsExperience > 0)
      );
    };
  }, [job]);

  // Memoized function to format work experience text
  const getWorkExp = useMemo(() => {
    return (currentJob) => {
      let workExp =
        currentJob?.minYearsExperience > 0
          ? currentJob?.minYearsExperience + " to "
          : "";

      workExp += currentJob?.maxYearsExperience + " Years of Exp";

      return workExp;
    };
  }, [job]);

  // Memoized function to get company logo
  const getCompanyLogo = useMemo(() => {
    return (company) => {
      return getCompanyLogoImage(company);
    };
  }, [job]);

  const bookmarkIcon = bookmark ? <UnsaveIcon /> : <SaveIcon />;

  // Handler for bookmark/unbookmark action
  function handleBookmarkJob(e) {
    e.stopPropagation();
    e.preventDefault();

    // Redirect to sign in if user is not logged in
    if (!loggedIn) {
      return dispatch(updateSelectedJob(job)).then(() => {
        dispatch(updateSignUpOrigin("save_job"));
        dispatch(updateShowSignInModalStatus(true));
      });
    }

    if (bookmarkLoading) {
      return;
    }

    setBookmarkLoading(true);

    // Toggle bookmark status
    if (!bookmark) {
      dispatch(bookmarkJobCompany({ jobId: job?.id })).then((res) => {
        if (res?.type === types.BOOKMARK_COMPANY_JOB_SUCCEED) {
          setBookmark(true);
          setBookmarkLoading(false);
        }
      });
    } else if (bookmark) {
      dispatch(unBookmarkJobCompany({ jobId: job?.id })).then((res) => {
        if (res?.type === types.UNBOOKMARK_COMPANY_JOB_SUCCEED) {
          setBookmark(false);
          setBookmarkLoading(false);
        }
      });
    }
  }

  useEffect(() => {
    if (bookmarkState !== undefined) {
      setBookmark(bookmarkState);
    }
  }, [bookmarkState]);

  return (
    <LinkWrapper href={`/jobs/${job?.slug}`} target="_blank">
      <JobCard key={job?.id}>
        <BoxContainer
          justifyContent="space-between"
          alignItems="center"
          marginBottom={"0.625rem"}
        >
          <JobTitle>{job?.title}</JobTitle>
          {job?.boosted ? (
            <JobBoostChip>
              <SparklesIconStyled />
              HOT
            </JobBoostChip>
          ) : null}
        </BoxContainer>

        <ChipContainer>
          {job?.jobType && (
            <JobDetailChip type="jobType">{job?.jobType}</JobDetailChip>
          )}
          {job?.stateRegion && (
            <JobDetailChip type="state">{job?.stateRegion}</JobDetailChip>
          )}
          {showWorkExp(job) && (
            <JobDetailChip type="salary">{getWorkExp(job)}</JobDetailChip>
          )}
        </ChipContainer>

        <BoxContainer justifyContent="space-between" alignItems="center">
          <CompanyDetailContainer>
            <CompanyLogo src={getCompanyLogo(job?.company)} />
            <CompanyName>{job?.company?.name}</CompanyName>
          </CompanyDetailContainer>
          {isClient && (
            <BookmarkButton
              onClick={handleBookmarkJob}
              disabled={bookmarkState === undefined}
            >
              {bookmarkIcon}
            </BookmarkButton>
          )}
        </BoxContainer>
      </JobCard>
    </LinkWrapper>
  );
}
